/**
 * Vulnerability Management API module — real per-domain split.
 *
 * Previously this file was a stub re-export from `../api`. It now hosts the
 * actual implementations for `vulnManagementApi` and `virtualPatchApi`,
 * extracted verbatim from the legacy `services/api.ts` monolith. The trim of
 * the source monolith happens in a follow-up; until then both locations
 * declare the same exports and `services/api/index.ts` decides which is
 * surfaced.
 *
 * Imports go to the sibling fetch client (`./client`) and unwrap helpers
 * (`./_unwrap`) directly instead of round-tripping through `../api`, matching
 * the established `cspm.ts` per-domain split pattern.
 */
import { apiClient as fetchClient } from './client';
import { _unwrap } from './_unwrap';
import {
  SeverityLevel,
  Vulnerability,
  CVEDetail,
  VulnScan,
} from '../../types';

// ============================================================================
// Vulnerability Management API
// ============================================================================

export const vulnManagementApi = {
  getVulnerabilities: async (params?: {
    severity?: SeverityLevel;
    exploitable_only?: boolean;
    kev_only?: boolean;
    resource_id?: string;
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<{ vulnerabilities: Vulnerability[]; total: number; page: number; page_size: number }> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities', params));
  },

  getVulnerability: async (id: string): Promise<Vulnerability> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/${id}`));
  },

  /**
   * Server-side aggregate over the WHOLE vulnerability set.
   *
   * The dashboard's headline tiles used to be computed by paging the entire
   * corpus into the browser (42 sequential requests for 8,396 rows) and
   * counting client-side, so the numbers appeared last and every chart under
   * them rendered a confident `0` in the meantime. This one call answers in
   * ~5ms and is the authority for totals — the paged list stays only for the
   * client-enriched facets that have no server column.
   */
  getStatistics: async (): Promise<{
    total_vulnerabilities: number;
    open: number;
    in_progress: number;
    resolved: number;
    suppressed: number;
    by_severity: Record<string, number>;
    by_exploit_maturity: Record<string, number>;
    active_campaigns: number;
    sla_policies: number;
    cve_database_size: number;
    kev_entries: number;
  }> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/statistics'));
  },

  getPrioritizedVulnerabilities: async (
    limit?: number
  ): Promise<{ vulnerabilities: any[]; total: number }> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/analytics', { limit }));
  },

  getCriticalVulnerabilities: async (
    limit?: number
  ): Promise<{ vulnerabilities: Vulnerability[] }> => {
    return _unwrap(await fetchClient.get('/api/v1/cves', { severity: 'critical', page_size: limit }));
  },

  getKEVVulnerabilities: async (
    limit?: number
  ): Promise<{ vulnerabilities: Vulnerability[] }> => {
    return _unwrap(await fetchClient.get('/api/v1/kev', { page_size: limit }));
  },

  getExploitableVulnerabilities: async (
    limit?: number
  ): Promise<{ vulnerabilities: Vulnerability[] }> => {
    return _unwrap(await fetchClient.get('/api/v1/cves', { exploit_maturity: 'weaponized', page_size: limit }));
  },

  updateVulnerabilityStatus: async (
    id: string,
    status: string,
    reason?: string
  ): Promise<{ status: string; vulnerability: Vulnerability }> => {
    const _qs = new URLSearchParams({ status }).toString();
    return _unwrap(await fetchClient.put(`/api/v1/vulnerabilities/${id}/status?${_qs}`, null));
  },

  getCVE: async (cveId: string): Promise<CVEDetail> => {
    return _unwrap(await fetchClient.get(`/api/v1/cves/${cveId}`));
  },

  getCVEInstances: async (
    cveId: string,
    limit?: number
  ): Promise<{ cve_id: string; instances: Vulnerability[]; total: number }> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities', { cve_id: cveId, page_size: limit }));
  },

  triggerScan: async (params: {
    scan_type?: string;
    target_resources?: string[];
  }): Promise<{ scan_id: string; status: string; message: string }> => {
    // Wiz-parity: unified data-plane primitive. Gateway proxies to
    // vulnerability-management /api/v1/vulnerabilities/scans/trigger.
    return _unwrap(await fetchClient.post('/api/v1/data-plane/refresh/vuln', params));
  },

  getScans: async (params?: {
    status?: string;
    limit?: number;
  }): Promise<{ scans: VulnScan[]; total: number }> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/scans', params));
  },

  getScan: async (scanId: string): Promise<VulnScan> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/scans/${scanId}`));
  },

  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/dashboard'));
  },

  // --- Vulnerability Management Service (port 8092) endpoints ---
  getVMDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/dashboard'));
  },
  getVMStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/stats'));
  },
  getExceptions: async (params?: { status?: string; exception_type?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/exceptions', params));
  },
  createException: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/exceptions', data));
  },
  getExceptionStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/exceptions/stats'));
  },
  approveException: async (id: string, approvedBy: string): Promise<any> => {
    const _qs = new URLSearchParams({ approved_by: approvedBy }).toString();
    return _unwrap(await fetchClient.put(`/api/v1/vulnerabilities/exceptions/${id}/approve?${_qs}`, null));
  },
  rejectException: async (id: string, reason: string): Promise<any> => {
    const _qs = new URLSearchParams({ reason }).toString();
    return _unwrap(await fetchClient.put(`/api/v1/vulnerabilities/exceptions/${id}/reject?${_qs}`, null));
  },
  expireException: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/vulnerabilities/exceptions/${id}/expire`));
  },
  sweepExpiredExceptions: async (): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/exceptions/sweep-expired'));
  },
  getSeverityOverrides: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/severity-overrides'));
  },
  overrideSeverity: async (vulnId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/vulnerabilities/${vulnId}/severity-override`, data));
  },
  getPatches: async (params?: { urgency?: string; patch_type?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/patches', params));
  },
  getPatchSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/patches/summary'));
  },
  getTrending: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/trending'));
  },
  getAnalytics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/analytics'));
  },
  getAffectedAssets: async (params?: { severity?: string; provider?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/assets', params));
  },
  getVulnGroups: async (params?: { group_by?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/groups', params));
  },
  getSources: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sources'));
  },
  getVMScans: async (params?: { scan_type?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/scans', params));
  },
  getVMScanSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/scans/summary'));
  },
  getExecutiveReport: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/reports/executive'));
  },
  getComplianceReport: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/reports/compliance'));
  },
  getCVEDatabase: async (params?: { severity?: string; is_kev?: boolean; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cves', params));
  },
  searchCVEs: async (query: string, params?: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cves/search', { query, ...params }));
  },
  getKEV: async (params?: { page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kev', params));
  },
  getKEVStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/kev/stats'));
  },
  getEPSS: async (cveId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/epss/${cveId}`));
  },
  getTopEPSS: async (params?: { limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/epss/top', params));
  },
  calculateRiskScore: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vuln/risk/score', data));
  },
  getVMCampaigns: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/campaigns'));
  },
  getSLAPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sla-policies'));
  },
  createSLAPolicy: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/sla-policies', data));
  },
  getSLABreaches: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sla/breaches'));
  },
  configureSLAAlerts: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/sla/configure-alerts', data));
  },
  getSLAAlertConfigs: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sla/alert-configs'));
  },
  getUpcomingSLABreaches: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sla/upcoming-breaches'));
  },

  // Suppress single vuln
  suppressVuln: async (vulnId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/vulnerabilities/${vulnId}/suppress`, data));
  },

  // Campaigns (detail)
  getCampaigns: async (params?: { status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/campaigns', params));
  },
  createCampaign: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/campaigns', data));
  },
  getCampaign: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/campaigns/${id}`));
  },
  updateCampaignStatus: async (id: string, status: string): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/vulnerabilities/campaigns/${id}/status`, { status }));
  },

  // Dedup & Enrich
  dedupVulns: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/dedup', data));
  },
  enrichVulns: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/enrich', data));
  },

  // Exception detail
  getException: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/exceptions/${id}`));
  },

  // Patch Recommendation
  getPatchRecommendation: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/${vulnId}/patch-recommendation`));
  },

  // Asset-level vuln details
  getAssetVulnerabilities: async (assetId: string, params?: { page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/assets/${assetId}/vulnerabilities`, params));
  },
  getAssetRiskProfile: async (assetId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/assets/${assetId}/risk-profile`));
  },
  rescanAsset: async (assetId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/vulnerabilities/assets/${assetId}/rescan`));
  },

  // Correlation
  getAssetToCVECorrelation: async (params?: { asset_id?: string; cve_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/correlation/asset-to-cve', params));
  },

  // Scan management
  cancelScan: async (scanId: string): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/vulnerabilities/scans/${scanId}/cancel`));
  },

  // Verifications
  getVerifications: async (params?: { status?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/verifications', params));
  },

  // EPSS Refresh
  refreshEPSS: async (): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/epss/refresh'));
  },

  // Predict Trending
  predictTrending: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/predict-trending'));
  },

  // Import Normalize
  normalizeImport: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/scans/import/normalize', data));
  },

  // VDP Submissions
  getVDPSubmissions: async (params?: { status?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vdp/submissions', params));
  },
  createVDPSubmission: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vdp/submissions', data));
  },

  // CVE Search (standalone)
  cveSearch: async (params: { keyword?: string; vendor?: string; product?: string; severity?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cve/search', params));
  },

  // NVD Sync Status
  nvdSyncStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/nvd/sync/status'));
  },

  // CPE Search
  cpeSearch: async (data: { keyword?: string; vendor?: string; product?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cpe/search', data));
  },

  // Vuln Group Detail
  getVulnGroupDetail: async (groupId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/groups/${groupId}`));
  },

  // NVD Sync
  nvdSync: async (): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/nvd/sync'));
  },

  // CPE Match
  cpeMatch: async (cpe: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/cpe/match/${encodeURIComponent(cpe)}`));
  },

  // Vulnerability Groups
  vulnGroups: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/groups'));
  },

  // Zero-Day & Emerging Threats
  getZeroDayAlerts: async (params?: { status?: string; severity?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/zero-day', params));
  },
  getZeroDayAlert: async (alertId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/zero-day/${alertId}`));
  },
  getZeroDayStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/zero-day/stats'));
  },
  acknowledgeZeroDay: async (alertId: string, acknowledgedBy: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/vulnerabilities/zero-day/${alertId}/acknowledge`, null, { acknowledged_by: acknowledgedBy }));
  },

  // Compliance Mapping
  getComplianceFrameworks: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/compliance/frameworks'));
  },
  getComplianceFramework: async (frameworkId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/compliance/frameworks/${frameworkId}`));
  },
  getComplianceSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/compliance/summary'));
  },
  getComplianceGaps: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/compliance/gaps'));
  },

  // Attack Paths
  getAttackPaths: async (params?: { min_risk?: number; internet_exposed?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/attack-paths', params));
  },
  getAttackPath: async (pathId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/attack-paths/${pathId}`));
  },
  getAttackPathStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/attack-paths/stats'));
  },

  // OS & Package Coverage
  getOSCoverage: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/coverage/os'));
  },
  getPackageCoverage: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/coverage/packages'));
  },
  getCoverageSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/coverage/summary'));
  },

  // Scanners
  getScanners: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/scanners'));
  },
  getScanResultsByAssetType: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/scan-results/by-asset-type'));
  },

  // Timeline & History
  getVulnTimeline: async (days?: number): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/timeline', { days }));
  },
  getMTTR: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/mttr'));
  },
  getRecurrence: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/recurrence'));
  },

  // SLA (Enhanced)
  getSLADashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sla/dashboard'));
  },
  getSLAOverdue: async (params?: { severity?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sla/overdue', params));
  },
  getSLAAging: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sla/aging'));
  },

  // Reports & Export
  getTrendReport: async (period?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/reports/trend', { period }));
  },
  exportVulnerabilities: async (format?: string, severity?: string): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/reports/export', null, { format, severity }));
  },
  getRiskSummaryReport: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/reports/risk-summary'));
  },

  // SBOM
  getSBOMSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sbom/summary'));
  },
  getSBOMComponents: async (params?: { ecosystem?: string; vulnerable_only?: boolean; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/sbom/components', params));
  },

  // Intelligence Feeds
  getIntelFeeds: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/feeds'));
  },
  syncFeed: async (feedId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/vulnerabilities/feeds/${feedId}/sync`));
  },

  // Grouping (Enhanced)
  getGroupsByPackage: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/groups/by-package'));
  },
  getGroupsByImage: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/groups/by-image'));
  },
  getGroupsByHost: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/groups/by-host'));
  },
  getGroupsByCluster: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/groups/by-cluster'));
  },

  // Advisories
  getAdvisories: async (params?: { vendor?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/advisories', params));
  },

  // Risk
  getRiskHeatmap: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/risk/heatmap'));
  },
  getTopRiskVulns: async (limit?: number): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/risk/top', { limit }));
  },

  // Policies
  getVulnPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/policies'));
  },

  // Search
  searchAll: async (q: string, scope?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/search', { q, scope }));
  },

  // KPIs
  getKPIs: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/metrics/kpis'));
  },

  // --- Ticketing Integrations ---
  createJiraTicket: async (data: {
    vulnerability_id: string;
    cve_id: string;
    project_key: string;
    issue_type?: string;
    priority?: string;
    summary?: string;
    description?: string;
    assignee?: string;
    labels?: string[];
    custom_fields?: Record<string, any>;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/integrations/jira/create-ticket', data));
  },
  syncJiraTickets: async (data?: { ticket_ids?: string[]; sync_direction?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/integrations/jira/sync', data || {}));
  },
  getJiraProjects: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/integrations/jira/projects'));
  },
  // Jira Bi-directional Sync
  getJiraSyncStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/integrations/jira/sync/status'));
  },
  triggerJiraSync: async (): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/integrations/jira/sync/trigger', {}));
  },
  getJiraSyncHistory: async (limit = 50, offset = 0): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/integrations/jira/sync/history?limit=${limit}&offset=${offset}`));
  },
  getJiraFieldMappings: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/integrations/jira/sync/mappings'));
  },
  createJiraFieldMapping: async (data: { jira_field: string; platform_field: string; direction?: string; transform?: any }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/integrations/jira/sync/mappings', data));
  },
  getJiraSyncConflicts: async (resolved?: boolean): Promise<any> => {
    const params = resolved !== undefined ? `?resolved=${resolved}` : '';
    return _unwrap(await fetchClient.get(`/api/v1/integrations/jira/sync/conflicts${params}`));
  },
  resolveJiraSyncConflict: async (conflictId: string, resolution: 'jira' | 'platform'): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/integrations/jira/sync/conflicts/${conflictId}/resolve`, { resolution }));
  },
  createServiceNowIncident: async (data: {
    vulnerability_id: string;
    cve_id: string;
    category?: string;
    subcategory?: string;
    impact?: number;
    urgency?: number;
    short_description?: string;
    description?: string;
    assignment_group?: string;
    configuration_item?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/integrations/servicenow/create-incident', data));
  },
  syncServiceNowIncidents: async (data?: { incident_ids?: string[]; sync_direction?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/integrations/servicenow/sync', data || {}));
  },
  getServiceNowCategories: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/integrations/servicenow/categories'));
  },
  sendSlackNotification: async (data: {
    channel: string;
    vulnerability_id?: string;
    cve_id?: string;
    message?: string;
    severity_filter?: string;
    include_details?: boolean;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/integrations/slack/notify', data));
  },
  configureSlackWebhook: async (data: {
    webhook_url: string;
    channel: string;
    bot_name?: string;
    notify_on?: string[];
    mention_users?: string[];
    enabled?: boolean;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/integrations/slack/configure', data));
  },
  getIntegrations: async (params?: { type?: string; enabled?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/integrations', params));
  },
  updateIntegration: async (integrationId: string, data: {
    name?: string;
    enabled?: boolean;
    config?: Record<string, any>;
  }): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/integrations/${integrationId}`, data));
  },
  deleteIntegration: async (integrationId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/integrations/${integrationId}`));
  },

  // --- FedRAMP Compliance Framework ---
  getFedRAMPStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/compliance/fedramp/status'));
  },
  getFedRAMPControls: async (params?: { family?: string; status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/compliance/fedramp/controls', params));
  },
  getFedRAMPPOAMs: async (params?: { status?: string; risk_level?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/compliance/fedramp/poams', params));
  },
  createFedRAMPPOAM: async (data: {
    vulnerability_id: string;
    cve_id: string;
    control_id: string;
    weakness_description: string;
    milestones: Array<{ description: string; due_date: string }>;
    scheduled_completion_date: string;
    risk_level?: string;
    resources_required?: string;
    vendor_dependency?: boolean;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/compliance/fedramp/poams', data));
  },
  getFedRAMPConMon: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/compliance/fedramp/continuous-monitoring'));
  },

  // --- Scheduled Reports ---
  createReportSchedule: async (data: {
    name: string;
    report_type?: string;
    frequency?: string;
    recipients: string[];
    format?: string;
    filters?: Record<string, any>;
    day_of_week?: number;
    day_of_month?: number;
    time_utc?: string;
    include_charts?: boolean;
    enabled?: boolean;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/reports/schedule', data));
  },
  getReportSchedules: async (params?: { enabled?: boolean; frequency?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/reports/schedules', params));
  },
  updateReportSchedule: async (scheduleId: string, data: {
    name?: string;
    frequency?: string;
    recipients?: string[];
    format?: string;
    filters?: Record<string, any>;
    day_of_week?: number;
    day_of_month?: number;
    time_utc?: string;
    include_charts?: boolean;
    enabled?: boolean;
  }): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/reports/schedules/${scheduleId}`, data));
  },
  deleteReportSchedule: async (scheduleId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/reports/schedules/${scheduleId}`));
  },
  generateReportNow: async (data?: {
    report_type?: string;
    format?: string;
    filters?: Record<string, any>;
    title?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/reports/generate-now', data || {}));
  },
  getReportHistory: async (params?: { report_type?: string; format?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/reports/history', params));
  },
  downloadReport: async (reportId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/reports/${reportId}/download`));
  },

  // --- Reachability Analysis ---
  getReachability: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/${vulnId}/reachability`));
  },
  analyzeReachability: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/vulnerabilities/${vulnId}/reachability/analyze`));
  },
  getReachabilitySummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/reachability/summary'));
  },
  getUnreachableVulns: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/reachability/unreachable'));
  },

  // --- Cross-Module Context ---
  getCrossModuleContext: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/${vulnId}/cross-module-context`));
  },
  getCSPMContext: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/${vulnId}/cspm-context`));
  },
  getCIEMContext: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/${vulnId}/ciem-context`));
  },
  getDSPMContext: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/${vulnId}/dspm-context`));
  },
  getCDRContext: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/${vulnId}/cdr-context`));
  },

  // --- Bulk Actions ---
  bulkUpdateStatus: async (data: { vulnerability_ids: string[]; status: string; reason?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/bulk/status', data));
  },
  bulkAssign: async (data: { vulnerability_ids: string[]; assignee: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/bulk/assign', data));
  },
  bulkSuppress: async (data: { vulnerability_ids: string[]; reason: string; duration_hours?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/bulk/suppress', data));
  },
  bulkTag: async (data: { vulnerability_ids: string[]; tags: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/bulk/tag', data));
  },

  // ========================================================================
  // Vulnerability Regression Detection
  // ========================================================================

  getRegressions: async (params?: { severity?: string; status?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/regressions', params));
  },

  getRecurrenceHistory: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/vulnerabilities/${vulnId}/recurrence-history`));
  },

  configureRegressionAlerts: async (data: { enabled: boolean; severity_threshold?: string; channels?: string[]; auto_notify?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/regression-alerts/configure', data));
  },

  getRegressionAlertConfigs: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/regression-alerts'));
  },

  // --- Verify Fix ---
  verifyVulnFix: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/vulnerabilities/${vulnId}/verify`));
  },

  // --- Rescan ---
  rescanVuln: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/vulnerabilities/${vulnId}/rescan`));
  },

  // --- Compound Risks ---
  getCompoundRisks: async (params?: { min_risk?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/compound-risks', params));
  },

  // --- Exploitability Validation (Wiz "Runtime Reachability" parity) ---
  // R39: 5 BE endpoints in reachability_exposure.py exposing PoC catalog +
  // exploitability scoring (CISA-KEV × EPSS × PoC × CVSS × reachable) +
  // network-firewall reachability check. None of these were wired in the FE.
  getExploitabilityStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/exploitability/stats'));
  },
  getValidatedExploits: async (params?: { exploitability_score?: string; cve_id?: string; skip?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/exploitability/validated', params));
  },
  getPocCatalog: async (params?: { cve_id?: string; verified_only?: boolean; skip?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/vulnerabilities/exploitability/poc-catalog', params));
  },
  validateExploitability: async (data: {
    cve_id: string;
    resource_id: string;
    resource_type?: string;
    cvss_score?: number;
    epss_score?: number;
    is_internet_facing?: boolean;
    target_port?: number;
    affected_version?: string;
    fixed_version?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/exploitability/validate', data));
  },
  checkReachability: async (data: {
    cve_id: string;
    resource_id: string;
    target_ip?: string;
    target_port?: number;
    is_internet_facing?: boolean;
    network_path?: any[];
    firewall_rules?: any[];
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/vulnerabilities/exploitability/check-reachability', data));
  },

  // --- Scanner Connectors ---
  getScannerConnectors: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/scanners/connectors'));
  },
  createScannerConnector: async (data: { name: string; type: string; config: Record<string, any> }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/scanners/connectors', data));
  },

  // --- Agentless Scan Profiles ---
  getAgentlessProfiles: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/scanning/agentless/profiles'));
  },
  createAgentlessProfile: async (data: { name: string; target_type: string; scan_options: Record<string, any> }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/scanning/agentless/profiles', data));
  },

  // --- Trigger Agentless Scan ---
  triggerAgentlessScan: async (data: { profile_id: string; account_ids?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/scanning/agentless/trigger', data));
  },

  // --- Custom Risk Scoring Formulas ---
  getRiskFormulas: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/risk-scoring/formulas'));
  },
  createRiskFormula: async (data: { name: string; formula: string; weights: Record<string, number> }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/risk-scoring/formulas', data));
  },
};

// ── Virtual Patching API ─────────────────────────────────────────────────────
// Migrated to the canonical fetch client (services/api/client.ts).
// Public signatures are unchanged: each method returns the unwrapped value
// and throws on non-2xx responses, so existing callers in
// VulnerabilityDashboard.tsx work without modification.
export const virtualPatchApi = {
  list: async (params?: { status?: string; patch_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/vulnerabilities/virtual-patches', params as any));
  },
  create: async (vulnId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/vulnerabilities/${vulnId}/virtual-patch`, data));
  },
  get: async (patchId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/vulnerabilities/virtual-patches/${patchId}`));
  },
  update: async (patchId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/vulnerabilities/virtual-patches/${patchId}`, data));
  },
  delete: async (patchId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/vulnerabilities/virtual-patches/${patchId}`));
  },
  getEffectiveness: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/vulnerabilities/virtual-patches/effectiveness'));
  },
  // R100: rollback a deployed WAF virtual patch. BE shipped at
  // vulnerability-management/k8s_iac_remediation.py:420 but no FE wrapper
  // method existed — variant 8 inside the API definition itself.
  rollback: async (patchId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/vulnerabilities/virtual-patches/${patchId}/rollback`));
  },
};
